import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Clock, DoorOpen } from "lucide-react";
import { formatTimeRange, pkDayKey } from "../lib/datetime";
import type { PDDaySummary } from "../types";

const CELLS = 42;

// The calendar grid is made of plain calendar dates (built from year, month
// and day), so a cell's key is read back the same way. A session's day is its
// Pakistan day (pkDayKey), whatever zone the device is set to.
function dayKey(date: Date): string {
  return date.toLocaleDateString("en-CA");
}

function markClass(day: PDDaySummary): string {
  if (day.status === "in_progress") return "bg-success";
  if (day.status === "ended") return "bg-archive-ink";
  return "bg-accent";
}

// A day box takes on whichever session is most "live": in progress beats
// scheduled beats already ended, so the calendar always highlights the
// thing the facilitator most needs to notice first.
function dayBoxClass(sessions: PDDaySummary[]): string {
  if (sessions.some((session) => session.status === "in_progress")) {
    return "border-2 border-success bg-success-bg";
  }
  if (sessions.some((session) => session.status !== "ended")) {
    return "border-2 border-accent bg-accent-soft";
  }
  return "border-2 border-archive-ink bg-archive-tint";
}

export function ScheduleCalendar({ days }: { days: PDDaySummary[] }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => {
    const [year, monthNumber] = pkDayKey(new Date()).split("-").map(Number);
    return new Date(year, monthNumber - 1, 1);
  });
  const [selected, setSelected] = useState("");

  const byDay = useMemo(() => {
    const map = new Map<string, PDDaySummary[]>();
    for (const day of days) {
      const key = pkDayKey(new Date(day.start));
      map.set(key, [...(map.get(key) || []), day]);
    }
    return map;
  }, [days]);

  const cells = useMemo(() => {
    const start = new Date(month.getFullYear(), month.getMonth(), 1 - month.getDay());
    return Array.from(
      { length: CELLS },
      (_, index) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + index)
    );
  }, [month]);

  // CLDR has no real narrow weekday for Urdu — it falls back to Latin
  // initials — so take the short form and clip it. Latin locales are
  // already one or two letters, so the clip is a no-op there.
  const weekdays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, index) => {
        const label = new Date(2024, 8, 1 + index).toLocaleDateString(i18n.language, {
          weekday: "short",
        });
        return { key: `wd-${index}`, label: label.slice(0, 3) };
      }),
    [i18n.language]
  );

  const today = pkDayKey(new Date());
  const selectedSessions = byDay.get(selected) || [];

  function shiftMonth(delta: number) {
    setMonth(new Date(month.getFullYear(), month.getMonth() + delta, 1));
    setSelected("");
  }

  // Same rule as the PD Days list: finished sessions open their summary.
  function open(day: PDDaySummary) {
    navigate(day.status === "ended" ? `/pd-day-progress/${day.id}` : `/pd-day/${day.id}`);
  }

  return (
    <section className="flex flex-col gap-3 rounded-xl bg-surface p-4 shadow-raised">
      <div className="flex items-center">
        <button
          type="button"
          className="grid h-9 w-9 place-items-center rounded-lg text-app-muted hover:bg-app-field"
          onClick={() => shiftMonth(-1)}
          aria-label={t("previousMonth")}
        >
          <ChevronLeft size={18} />
        </button>
        <h2 className="flex-1 text-center text-sm font-semibold text-app-ink">
          {month.toLocaleDateString(i18n.language, { month: "long", year: "numeric" })}
        </h2>
        <button
          type="button"
          className="grid h-9 w-9 place-items-center rounded-lg text-app-muted hover:bg-app-field"
          onClick={() => shiftMonth(1)}
          aria-label={t("nextMonth")}
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="grid grid-cols-7 text-center text-[10px] font-semibold text-app-muted">
        {weekdays.map(({ label, key }) => (
          <span key={key} className="truncate">
            {label}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((date) => {
          const key = dayKey(date);
          const sessions = byDay.get(key) || [];
          const inMonth = date.getMonth() === month.getMonth();
          const isToday = key === today;
          const isSelected = key === selected;
          return (
            <button
              type="button"
              key={key}
              disabled={sessions.length === 0}
              onClick={() => setSelected(isSelected ? "" : key)}
              aria-label={
                sessions.length
                  ? t("calendarDayWithSessions", {
                      date: date.toLocaleDateString(i18n.language),
                      n: sessions.length,
                    })
                  : date.toLocaleDateString(i18n.language)
              }
              className={`relative grid h-10 place-items-center rounded-lg text-sm transition-colors disabled:cursor-default ${
                inMonth ? "text-app-ink" : "text-app-icon"
              } ${isSelected ? "bg-accent-soft font-semibold text-accent" : ""} ${
                isToday && !isSelected ? "ring-1 ring-accent" : ""
              } ${
                sessions.length && !isSelected
                  ? `font-semibold hover:brightness-95 ${dayBoxClass(sessions)}`
                  : sessions.length
                    ? "font-semibold"
                    : ""
              }`}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3 border-t border-app-line pt-3 text-[11px] text-app-muted">
        <span className="inline-flex items-center gap-1.5">
          <i className="h-2 w-2 rounded-full bg-accent" />
          {t("legendScheduled")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="h-2 w-2 rounded-full bg-success" />
          {t("legendInProgress")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="h-2 w-2 rounded-full bg-archive-ink" />
          {t("legendEnded")}
        </span>
      </div>

      {selected && (
        <div className="flex flex-col gap-2 rounded-xl bg-app-field p-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-app-muted">
            {new Date(`${selected}T00:00:00`).toLocaleDateString(i18n.language, {
              weekday: "long",
              day: "numeric",
              month: "long",
            })}
          </p>
          {selectedSessions.map((session) => (
            <button
              key={session.id}
              type="button"
              onClick={() => open(session)}
              className="flex flex-col gap-1 rounded-lg bg-surface p-3 text-start shadow-raised"
            >
              <span className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${markClass(session)}`} />
                <span className="truncate text-sm font-semibold text-app-ink">
                  {session.center}
                </span>
              </span>
              <span className="flex flex-wrap items-center gap-x-3 text-xs text-app-muted">
                <span className="inline-flex items-center gap-1" dir="ltr">
                  <Clock size={12} />
                  {formatTimeRange(session.start, session.end, i18n.language)}
                </span>
                {session.room && (
                  <span className="inline-flex items-center gap-1">
                    <DoorOpen size={12} />
                    {session.room}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
